/**
 * The one way the app talks to its API (PLAN §14).
 *
 * Everything goes through `request`, so three rules hold everywhere without
 * each caller remembering them: the CSRF header is always sent, the single
 * `{ error: { code, message } }` shape is always unwrapped, and an expired
 * session always surfaces as one recognisable error rather than a confusing
 * 401 body.
 */

/** PLAN §15: every state-changing request carries this header. */
const CSRF_HEADER = 'X-Requested-With';
const CSRF_VALUE = 'dintiko';

const SAFE_METHODS = new Set(['GET', 'HEAD']);

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

/**
 * An error the owner can be shown as-is. The API already writes its messages
 * in plain language that says what to do next (CLAUDE.md), so the UI never has
 * to invent wording for a server failure.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  /** The session is gone — the router sends these to /login rather than retrying. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Nothing is reachable. Worth saying "you appear to be offline" instead of a code. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Query parameters; undefined and null entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `/api${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers();

  // Sent on every request, not only unsafe ones: it costs nothing and means a
  // method change can never silently drop the protection.
  headers.set(CSRF_HEADER, CSRF_VALUE);
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');

  let response: Response;
  try {
    const init: RequestInit = {
      method,
      headers,
      // The session cookie is HttpOnly, so the browser must be told to send it.
      credentials: 'same-origin',
    };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;

    response = await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    // fetch only rejects when the network itself failed. On a phone on Ugandan
    // mobile data that is common enough to deserve its own message.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, {
      code: 'OFFLINE',
      message: 'Cannot reach the server. Check your connection and try again.',
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, errorBodyOf(parsed, response.status));
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * The API always answers with `{ error: { code, message } }`, but a proxy or a
 * crash can produce something else — so a failure is never allowed to become an
 * unreadable "[object Object]".
 */
function errorBodyOf(parsed: unknown, status: number): ApiErrorBody {
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    'error' in parsed &&
    typeof (parsed as { error: unknown }).error === 'object' &&
    (parsed as { error: unknown }).error !== null
  ) {
    const error = (parsed as { error: Record<string, unknown> }).error;
    if (typeof error.code === 'string' && typeof error.message === 'string') {
      return { code: error.code, message: error.message, details: error.details };
    }
  }

  if (status === 401) {
    return { code: 'UNAUTHORIZED', message: 'Your session has ended. Sign in again.' };
  }

  return {
    code: 'UNEXPECTED',
    message: `Something went wrong (${String(status)}). Try again, and tell the developer if it keeps happening.`,
  };
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, query === undefined ? {} : { query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Exported for the tests that assert the CSRF contract. */
export const csrf = { header: CSRF_HEADER, value: CSRF_VALUE, safeMethods: SAFE_METHODS };
